import React, { useEffect, useState, useRef } from "react";
import { useContext } from "react";
import studyviewerContext from "../../../../context/studyviewer/StudyviewerContext";
import ViewportContext from "../../../../context/studyviewer/ViewportContext";
import studyReportContext from "../../../../context/studyreport/StudyReportContext";
import ThumbnailContext from "../../../../context/studyviewer/ThumbnailContext";
import themeContext from "../../../../context/theme/ThemeContext";
import toastContext from "../../../../context/toast/ToastContext";
import { TOAST_TYPE } from "../../../../constants/Constants";
import Copy from "../../../../components/SVGComponents/Copy";

/**
 * The impressions panel: every open study's impression, opened study first.
 *
 * Used by both viewers, which need different things from it.
 *
 * The basic viewer owns its thumbnails, so hovering a thumbnail and hovering a row
 * highlight each other through ThumbnailContext, and a row needs no more than a date --
 * the thumbnail panel beside it already says which study is which.
 *
 * The Advance Viewer's thumbnails belong to MedDream and are out of reach, so it passes
 * `highlightStudyId` (the study in the focused viewport, reported over the postMessage
 * channel -- see helpers/MedDreamIntegration) and turns on `showStudyDetails`, which adds
 * a modality/description line and a copy button so a row can be tied to a study without a
 * list to cross-reference. `anchored` hangs the panel off the toolbar button rather than
 * the top-left of the image area, which in that viewer is MedDream's own panel.
 */
const Impressions = ({ highlightStudyId, anchored, showStudyDetails }) => {
  const { impressions } = useContext(studyviewerContext);
  const { showImpressions, setShowImpressions } = useContext(ViewportContext);
  const { getAlterColor, themeName, getAlterClass } = useContext(themeContext);
  const { showToastv2 } = useContext(toastContext);

  const { getConsistentDateFormat } = useContext(studyReportContext);
  const {
    impressionHoveredStudyId,
    setImpressionHoveredStudyId,
    setSeriesHovered,
    seriesHovered,
  } = useContext(ThumbnailContext);

  const [isVisible, setIsVisible] = useState(showImpressions);
  const impressionsContainerRef = useRef(null);

  let timer;

  const onHovered = (key) => {
    timer = setTimeout(() => {
      setImpressionHoveredStudyId(impressions[key].studyId);
      setSeriesHovered(false);
    }, 300);
  };

  const onHoveredOut = () => {
    clearTimeout(timer);
    setImpressionHoveredStudyId(0);
  };

  const scrollTo = (studyId) => {
    const element = document.getElementById("viewport-impression-" + studyId);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
    }
  };

  const copyImpression = (event, studyId) => {
    event.preventDefault();
    event.stopPropagation();
    const element = document.getElementById("viewport-impression-text-" + studyId);
    if (!element) return;
    // execCommand rather than navigator.clipboard: the viewer is served over plain HTTP on
    // some deployments, where the async clipboard API is unavailable.
    const range = document.createRange();
    range.selectNode(element);
    window.getSelection().removeAllRanges();
    window.getSelection().addRange(range);
    document.execCommand("copy");
    window.getSelection().removeAllRanges();
    showToastv2(TOAST_TYPE.SUCCESS, "Copied to clipboard!");
  };

  /** A one-line "CT · CHEST" style identifier, skipping whatever the study lacks. */
  const studyIdentity = (entry) =>
    [entry.modality, entry.studyDescription || entry.bodyPart]
      .filter((part) => part != null && String(part).trim().length > 0)
      .join(" · ");

  useEffect(() => {
    if (seriesHovered) {
      scrollTo(impressionHoveredStudyId);
    }
  }, [impressionHoveredStudyId]);

  // Advance Viewer: follow the focused viewport, so that clicking a prior brings its
  // impression into view instead of leaving the radiologist to find it in the list.
  useEffect(() => {
    if (isVisible && highlightStudyId) {
      scrollTo(highlightStudyId);
    }
  }, [highlightStudyId, isVisible]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        impressionsContainerRef.current &&
        !impressionsContainerRef.current.contains(event.target)
      ) {
        setIsVisible(false);
        setShowImpressions(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Advance Viewer: the click-outside handler above never sees a click on the images,
  // because those land inside MedDream's cross-origin iframe and a click there does not
  // reach this document at all. Clicking the surrounding Augmento chrome closed the panel
  // while clicking a viewport left it open, which reads as the panel being stuck.
  //
  // Focus is the one signal that does cross: giving the iframe focus blurs this window.
  // So a blur whose new activeElement is an iframe means "the radiologist has clicked into
  // the viewer", and the panel should close -- while the click still reaches MedDream,
  // unlike a transparent backdrop, which would swallow it.
  useEffect(() => {
    if (!isVisible || !anchored) return undefined;

    const handleWindowBlur = () => {
      // Read activeElement after the focus change has settled: during the blur event
      // itself the browser may still report the previously focused element.
      setTimeout(() => {
        // Switching tabs or applications also blurs the window, but leaves activeElement
        // where it was -- only an iframe taking focus means the viewer was clicked.
        if (document.activeElement?.tagName === "IFRAME") {
          setIsVisible(false);
          setShowImpressions(false);
        }
      }, 0);
    };

    window.addEventListener("blur", handleWindowBlur);
    return () => window.removeEventListener("blur", handleWindowBlur);
  }, [isVisible, anchored]);

  useEffect(() => {
    setIsVisible(showImpressions);
  }, [showImpressions]);

  const renderImpressionText = (entry) => (
    <span
      id={`viewport-impression-text-${entry.studyId}`}
      className="viewport-impression-text-value"
      // "not reported yet" / "not available" are placeholders, not findings -- dimmed so a
      // real impression stands out in a list that is mostly priors.
      style={
        entry.hasImpression === false
          ? { fontStyle: "italic", opacity: 0.6 }
          : undefined
      }
      dangerouslySetInnerHTML={{ __html: entry.impressionText }}
    ></span>
  );

  return (
    <>
      {isVisible && (
        <div
          ref={impressionsContainerRef}
          className={`viewport-impressions-container modal-scroll ${
            anchored ? "impressions-anchored" : ""
          }`}
        >
          {Object.keys(impressions).length > 0 &&
            Object.keys(impressions).map((key, index) => {
              const entry = impressions[key];
              const isHighlighted =
                impressionHoveredStudyId === entry.studyId ||
                (highlightStudyId != null && highlightStudyId === entry.studyId);
              const identity = studyIdentity(entry);

              return (
                <React.Fragment key={key}>
                  {index > 0 && (
                    <div
                      className={getAlterClass(
                        themeName,
                        "viewport-impressions-study-date"
                      )}
                    ></div>
                  )}
                  <div
                    id={`viewport-impression-${entry.studyId}`}
                    style={{ color: getAlterColor("#D5D5D5", "") }}
                    className={`viewport-impressions-text ${
                      isHighlighted ? "highlight-impression" : ""
                    }`}
                    onMouseEnter={() => onHovered(key)}
                    onMouseLeave={onHoveredOut}
                  >
                    {showStudyDetails && identity && (
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          marginBottom: "2px",
                        }}
                      >
                        <span className="viewport-impression-identity">
                          {identity}
                        </span>
                        {entry.isCurrent && (
                          <span
                            className="viewport-impression-current-tag"
                            style={{ color: getAlterColor("#7EC8FF", "") }}
                          >
                            Current
                          </span>
                        )}
                      </span>
                    )}
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "8px",
                      }}
                    >
                      <span
                        className="viewport-impression-date"
                        style={{ color: getAlterColor("#ABABAB", "") }}
                      >
                        {getConsistentDateFormat(
                          showStudyDetails
                            ? entry.studyDate || entry.uploadDate
                            : entry.uploadDate
                        )}
                      </span>
                      {showStudyDetails && entry.hasImpression !== false && (
                        <span
                          className="cursor-pointer"
                          style={{ display: "flex", flexShrink: 0 }}
                          title="Copy impression"
                          onClick={(e) => copyImpression(e, entry.studyId)}
                        >
                          <Copy />
                        </span>
                      )}
                    </span>
                    <span style={{ height: "5px" }}></span>
                    {renderImpressionText(entry)}
                  </div>
                </React.Fragment>
              );
            })}
        </div>
      )}
    </>
  );
};

export default Impressions;
